import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { site } from "../content";
import { scrollToId } from "../lib/scroll";
import { waLink, trackWhatsAppClick } from "../lib/whatsapp";

const NAV = [
  { label: "Como funciona", id: "como-funciona" },
  { label: "Benefícios", id: "beneficios" },
  { label: "Modelos", id: "modelos" },
  { label: "Especificações", id: "especificacoes" },
  { label: "FAQ", id: "faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed inset-x-0 top-8 z-[55] transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-ink/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-[72px] md:px-12">
        <button
          onClick={() => go("hero")}
          data-testid="header-logo"
          className="flex items-center transition-opacity duration-300 hover:opacity-85"
          aria-label="Barber Steamer — voltar ao topo"
        >
          <img
            src={site.assets.logo}
            alt="Barber Steamer — o verdadeiro aquecedor de toalhas para barbearia"
            className="h-10 w-auto md:h-12"
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => go(item.id)}
              className="text-xs font-semibold uppercase tracking-[0.18em] text-bone/60 transition-colors duration-300 hover:text-gold"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink(site.messages.menu)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            onClick={() => trackWhatsAppClick("header", "Quero meu Barber Steamer")}
            className="hidden rounded-full bg-gold px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-gold-light sm:inline-flex"
          >
            Quero meu Barber Steamer
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-bone lg:hidden"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => go(item.id)}
                  className="rounded-lg px-3 py-3.5 text-left font-display text-sm font-bold uppercase tracking-wider text-bone/80 transition-colors duration-200 hover:bg-white/5 hover:text-gold"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={waLink(site.messages.menu)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-cta"
                onClick={() => trackWhatsAppClick("header_mobile", "Quero meu Barber Steamer")}
                className="mt-4 inline-flex items-center justify-center rounded-full bg-gold px-5 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink"
              >
                Quero meu Barber Steamer
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
